import * as React from 'react';
import {
  FloatingTree,
  Strategy,
  useFloatingParentNodeId,
} from '@floating-ui/react';
import { usePopover } from './hooks/usePopover/usePopover';
import { PopoverPlacement } from '.';

type PopoverContext = {
  renderCloseButton: boolean;
  onClose?: () => any;
  onOpenChange: (isOpen: boolean) => void;
} & ReturnType<typeof usePopover>;

const PopoverContext = React.createContext<PopoverContext | null>(null);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

export type PopoverProps = {
  children: React.ReactNode;
  isOpen?: boolean;
  closeOnBlur?: boolean;
  closeOnEscape?: boolean;
  renderCloseButton?: boolean;
  strategy?: Strategy;
  placement?: PopoverPlacement;
  forcePlacement?: boolean;
  onClose?: () => any;
  onOpenChange?: (isOpen: boolean) => any;
};

export const Popover = ({
  children,
  isOpen,
  placement = 'bottom',
  forcePlacement = false,
  closeOnBlur = true,
  closeOnEscape = false,
  renderCloseButton = true,
  strategy = 'absolute',
  onOpenChange = () => '',
  onClose,
}: PopoverProps) => {
  const parentId = useFloatingParentNodeId();
  const popover = usePopover({
    onClose,
    placement,
    forcePlacement,
    closeOnBlur,
    closeOnEscape: closeOnEscape,
    controlledIsOpen: isOpen,
    strategy,
  });
  const contextValue: PopoverContext = {
    ...popover,
    renderCloseButton,
    onClose,
    onOpenChange,
  };
  const Content = (
    <PopoverContext.Provider value={contextValue}>
      {children}
    </PopoverContext.Provider>
  );

  // Wrap the root floating element in a tree component to allow for nested floating elements
  // Child floating elements will appear in the same tree
  return parentId === null ? <FloatingTree>{Content}</FloatingTree> : Content;
};
